import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningIcon from '@mui/icons-material/Warning';
import { Accordion, AccordionDetails, AccordionSummary, Grid, Link, Typography } from '@mui/material';
import CohortQueryList from 'components/SearchFilters/CohortQueryList';
import {
  compact,
  filter,
  includes,
  isArray,
  isEmpty,
  isNull,
  isUndefined,
  join,
  map,
  some,
  toPairs,
  toString,
  uniq,
} from 'lodash';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { PrepareCustomerResultsJobWithUnpublishedExperimentResultIds } from 'api/customerResults';
import CohortQueryListItem from 'components/SearchFilters/CohortQueryListItem';
import {
  isLegacyPrepareCustomerResultsManifest,
  isPrepareCustomerResultsChildJob,
  isPrepareCustomerResultsParentJob,
  PrepareCustomerResultsParams,
} from 'interfaces/job';
import { Permission } from 'interfaces/permissionOption';
import { humanize } from 'utils/helpers';
import { normalizeKeysToCamelCase } from 'utils/objectCase';
import { manualSelectionFilters } from 'utils/useCasesParams/constants';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { useNavigationToViewerPage } from 'utils/useNavigationToViewerPage';
import { usePermissions } from 'utils/usePermissions';
import { PrepareCustomerResultsJobManifestTable } from './PrepareCustomerResultsJobManifestTable';
import { PrepareCustomerResultsJobSnapshotTable } from './PrepareCustomerResultsJobSnapshotTable';
import { stringToDisplay } from './utils';

const rawValueFields: Array<keyof PrepareCustomerResultsParams> = [
  'resultTypesToExport',
  'canViewInternalLabels',
  'zipGranularity',
  'generateFeaturesCsvFile',
  'publishResultsToCustomer',
  'unitsPerZip',
  'shardLimit',
];

export const PrepareCustomerResultsJobParams: React.FC<{
  job: Partial<PrepareCustomerResultsJobWithUnpublishedExperimentResultIds>;
}> = ({ job }) => {
  const { hasPermission } = usePermissions();
  const isNucleaiUser = hasPermission(Permission.UseNucleaiExperimentalFeatures);
  const isChildJob = isPrepareCustomerResultsChildJob(job);
  const isParentJob = isPrepareCustomerResultsParentJob(job);
  const normalizedParams = normalizeKeysToCamelCase(job?.params || {}) as Partial<PrepareCustomerResultsParams> & {
    parentJobId?: string;
    workerIndex?: number;
    workerCount?: number;
    plannedUnits?: unknown[];
  };
  const studyId = job?.studyId;
  const manifest = job?.manifest;
  const queryObject = normalizedParams.queryObject;
  const parentJobId = job?.parentId || normalizedParams.parentJobId;
  const workerIndex = typeof normalizedParams.workerIndex === 'number' ? normalizedParams.workerIndex + 1 : undefined;
  const workerCount = typeof normalizedParams.workerCount === 'number' ? normalizedParams.workerCount : undefined;
  const plannedUnitsCount = isArray(normalizedParams.plannedUnits) ? normalizedParams.plannedUnits.length : undefined;

  const hasSpecificSelections = some(manualSelectionFilters, (filterName) => !isEmpty(queryObject?.[filterName]));

  const snapshot = normalizedParams.snapshot;

  const unpublishedExperimentResultIds = compact(job?.unpublishedExperimentResultIds);

  const { getUrlToSlidePage } = useNavigationToViewerPage();

  const { labId } = useCurrentLabId();

  const rawValues = filter(
    toPairs(normalizedParams),
    ([key, value]) =>
      includes(rawValueFields, key as keyof PrepareCustomerResultsParams) &&
      !(isArray(value) && isEmpty(value)) &&
      !isUndefined(value) &&
      !isNull(value)
  );

  return (
    <>
      {(isChildJob || isParentJob) && (
        <Grid item marginBlock={2}>
          <Grid item>
            <Typography variant="body1">Job Role: {isChildJob ? 'Shard child' : 'Parent export'}</Typography>
          </Grid>
          {isChildJob && parentJobId && (
            <Grid item>
              <Typography variant="body1">Parent Job Id: {toString(parentJobId)}</Typography>
            </Grid>
          )}
          {isChildJob && workerIndex && workerCount && (
            <Grid item>
              <Typography variant="body1">
                Shard: {workerIndex} of {workerCount}
              </Typography>
            </Grid>
          )}
          {isChildJob && plannedUnitsCount !== undefined && (
            <Grid item>
              <Typography variant="body1">Planned Units: {plannedUnitsCount}</Typography>
            </Grid>
          )}
        </Grid>
      )}
      {manifest &&
        (isEmpty(manifest) ? (
          <Grid item>
            <Grid item>
              <Typography variant="body1">Export contents:</Typography>
            </Grid>
            <Grid item>
              <Typography variant="body1">None</Typography>
            </Grid>
          </Grid>
        ) : isLegacyPrepareCustomerResultsManifest(manifest) ? (
          <Grid item marginBlock={2}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Grid item container>
                  <Grid item>
                    <Typography variant="body1">{isChildJob ? 'Shard Slide IDs' : 'Slide IDs'}</Typography>
                  </Grid>
                  {!isChildJob && isNucleaiUser && (
                    <Grid item container direction="row" alignItems="center" mt={1} spacing={1}>
                      <Grid item xs={1} mr={1}>
                        <WarningIcon />
                      </Grid>
                      <Grid item xs={10}>
                        <Typography variant="caption">
                          This is a legacy export - cannot verify if results are still published
                        </Typography>
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </AccordionSummary>
              <AccordionDetails>
                {map(uniq(manifest), (slideId) => (
                  <Grid item key={slideId}>
                    <Link
                      to={getUrlToSlidePage({ slideId, labId, caseStudyId: studyId! })}
                      target="_blank"
                      rel="noopener noreferrer"
                      component={RouterLink}
                    >
                      <Typography variant="caption" key={slideId}>
                        {slideId}
                      </Typography>
                    </Link>
                  </Grid>
                ))}
              </AccordionDetails>
            </Accordion>
          </Grid>
        ) : (
          studyId && (
            <Grid item marginBlock={2}>
              <PrepareCustomerResultsJobManifestTable
                manifest={manifest}
                studyId={studyId!}
                unpublishedExperimentResultIds={unpublishedExperimentResultIds}
                unitsPerZip={normalizedParams.unitsPerZip}
                zipGranularity={normalizedParams.zipGranularity}
              />
            </Grid>
          )
        ))}
      {snapshot &&
        (isEmpty(snapshot) ? (
          <Grid item>
            <Grid item>
              <Typography variant="body1">Query Snapshot: </Typography>
            </Grid>
            <Grid item>
              <Typography variant="body1">None</Typography>
            </Grid>
          </Grid>
        ) : (
          studyId && (
            <Grid item marginBlock={2}>
              <PrepareCustomerResultsJobSnapshotTable snapshot={snapshot} studyId={studyId!} />
            </Grid>
          )
        ))}
      {queryObject && (
        <Grid item marginBlock={2}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body1">Filters:</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ 'overflow-x': 'auto' }}>
              {hasSpecificSelections ? (
                map(
                  manualSelectionFilters,
                  (filterName) =>
                    !isEmpty(queryObject?.[filterName]) && (
                      <CohortQueryListItem
                        showIcon
                        key={filterName}
                        itemKey={filterName}
                        value={`${humanize(filterName)}: ${join(queryObject?.[filterName], ', ')}`}
                      />
                    )
                )
              ) : (
                <CohortQueryList queryObject={queryObject} showIcon />
              )}
            </AccordionDetails>
          </Accordion>
        </Grid>
      )}
      {!isEmpty(rawValues) && (
        <Grid item marginBlock={2}>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body1">Additional Job Params:</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container direction="column">
                {map(rawValues, ([fieldName, value]) => (
                  <Grid item key={fieldName}>
                    <Grid item>
                      <Typography variant="body2" color="textSecondary">
                        {humanize(fieldName)}: {toString(stringToDisplay(value))}
                      </Typography>
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>
      )}
    </>
  );
};
